import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete'
import { IVetClinic } from '@app/models/vet-clinic'
import { WorkflowStep } from '@app/models/workflow-step'
import { StepperService } from '@app/services/stepper.service'
import { StoreService } from '@app/services/store.service'
import { OrchestratorService } from '@services/orchestrator.service'
import { debounceTime, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs'
import { filter, map, startWith, tap } from 'rxjs/operators'

@Component({
	selector: 'mv-vet-clinic',
	templateUrl: './vet-clinic.component.html',
	styleUrls: ['./vet-clinic.component.scss'],
})
export class VetClinicComponent implements OnInit, AfterViewInit, OnDestroy {
	@Output() _emb_close: EventEmitter<boolean> = new EventEmitter<boolean>()
	@ViewChild('clinicInputEl') clinicInputEl!: ElementRef<HTMLInputElement>
	@ViewChild('zipInputEl') zipInputEl!: ElementRef<HTMLInputElement>
	@ViewChild(MatAutocompleteTrigger) clinicListTrigger!: MatAutocompleteTrigger
	@ViewChild('clinicList') clinicListEl?: MatAutocomplete
	public vetClinicForm: FormGroup
	public clinicInputHidden: boolean = true
	public disableAutocomplete: boolean = true
	public popoverVisible: boolean = false
	public petName$: Observable<string>
	public filteredOptions$!: Observable<IVetClinic[]> | undefined
	public noSelectionMadeError: boolean = false
	public invalidZipCode: boolean = false
	public matchValue: string = ''
	private readonly workflowStep: number = 1
	private clinicList: IVetClinic[] = []
	private readonly zipLength: number = 5
	/** The number of milliseconds the input will wait to send the user to the next input */
	private readonly zipInputDebounceTime: number = 750
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject()

	constructor(
		private readonly fb: FormBuilder,
		private readonly orchestratorService: OrchestratorService,
		private readonly stepperService: StepperService,
		private readonly storeService: StoreService
	) {
		this.petName$ = this.storeService.appState$.pipe(
			filter(s => s.petId !== undefined),
			map(s => s.petName ?? 'Your Pet')
		)
		this.vetClinicForm = this.fb.group({
			zipCode: [null, [Validators.required, Validators.maxLength(this.zipLength), Validators.minLength(this.zipLength)]],
			clinic: [null, Validators.required],
		})
	}

	private get clinicControl(): AbstractControl | null {
		return this.vetClinicForm.get('clinic')
	}

	private get zipCodeControl(): AbstractControl | null {
		return this.vetClinicForm.get('zipCode')
	}

	@HostListener('window:emb_mv:global_update')
	private handleClose(): void {
		this.popoverVisible = false
	}

	async ngOnInit(): Promise<void> {
		this.stepperService.updateStep(this.workflowStep)

		const { zipCode = null } = await firstValueFrom(this.storeService.formState$)
		if (!!zipCode) {
			this.vetClinicForm.patchValue({
				zipCode,
			})
			this.enableVetSearch()
		}

		this.filteredOptions$ = this.clinicControl?.valueChanges.pipe(
			startWith(''),
			map(v => (typeof v === 'string' ? v : v?.clinicName)),
			map(v => this.filterClinics(<string>v))
		)
	}

	ngAfterViewInit(): void {
		this.zipCodeControl?.valueChanges
			.pipe(
				debounceTime(this.zipInputDebounceTime),
				filter(v => !!v),
				takeUntil(this.ngUnsubscribe$)
			)
			// eslint-disable-next-line @typescript-eslint/no-misused-promises
			.subscribe(async value => {
				if (value.length === this.zipLength) {
					this.clinicList = await firstValueFrom(this.orchestratorService.getVetsGivenZip(<string>value)).catch(() => {
						this.invalidZipCode = true
						return []
					})
					if (this.clinicList.length > 0) {
						this.invalidZipCode = false
						this.enableVetSearch()
					}
				}
			})

		this.clinicControl?.valueChanges
			.pipe(
				takeUntil(this.ngUnsubscribe$),
				tap(v => {
					if (typeof v !== 'string' && v?.id === 'notListed') {
						this.popoverVisible = true
					}
				}),
				map(value => !value || value.length < 1)
			)
			.subscribe(v => {
				if (v) {
					setTimeout(() => {
						this.clinicListTrigger.closePanel()
					})
				}
			})
	}

	public optionDisplayValue(vet: IVetClinic): string {
		const displayValue: string = vet && vet.accountName ? vet.accountName : ''

		return displayValue
	}

	public checkForSelection(): void {
		const hasMatch = this.clinicList.map(i => i.accountId).includes(<string>this.clinicControl?.value?.accountId)

		if (!hasMatch) {
			this.showAddVetPopover()
			this.clinicControl?.patchValue(null, { emitEvent: false })
		}
	}

	public handleSelection(ev: MatAutocompleteSelectedEvent): void {
		const { value } = ev.option
		if (value === 'notListed') {
			this.popoverVisible = true
			this.clinicControl?.setValue('')
			this.clinicInputEl?.nativeElement.blur()

			return
		}

		this.popoverVisible = false
	}

	public propegateDismiss(ev: KeyboardEvent): void {
		if (ev.key !== 'Escape') return
		this.popoverVisible = false
	}

	public showAddVetPopover(): void {
		this.popoverVisible = !this.popoverVisible
	}

	public handleAddNewVet(ev: MouseEvent): void {
		ev.preventDefault()
		// FIXME: Update to new pattern
		// this.store.dispatch(updateFormState({
		// 	cannotFindVet: true,
		// }))
		// this.router.navigate(['add', 'new-vet'])
		this.storeService.updateWorkflowStep(WorkflowStep.AddNewVet)
	}

	public nextStep(): void {
		this.checkForSelection()
		if (this.vetClinicForm.invalid) return

		this.storeService.updateFormState({
			cannotFindVet: false,
			zipCode: this.zipCodeControl?.value,
			vetClinic: this.clinicControl?.value,
		})
		this.storeService.updateWorkflowStep(WorkflowStep.OtherOwners)
	}

	public stepBack(): void {
		this.popoverVisible = false
		this.enableZipEntry()
	}

	public cancel(): void {
		this.resetForm()
		this.popoverVisible = false
		this._emb_close.emit(false)
		this.storeService.cleanupFormState()
	}

	public resetForm(): void {
		this.vetClinicForm.reset()
		this.zipCodeControl?.enable()
		this.invalidZipCode = false
		this.clinicInputHidden = true
	}

	private enableZipEntry(): void {
		this.zipCodeControl?.reset()
		this.zipCodeControl?.enable()
		this.zipInputEl.nativeElement.focus()
		this.clinicInputHidden = true
		this.clinicControl?.reset()
	}

	private enableVetSearch(): void {
		this.clinicInputHidden = false
		this.clinicInputEl.nativeElement.focus()
		this.zipCodeControl?.disable({ emitEvent: false })
		this.clinicListTrigger.closePanel()
	}

	// FIXME: this will need to change to match the shape of a vet clinic
	private filterClinics(value: string): IVetClinic[] {
		this.matchValue = value
		const filterValue = this.normalizeValue(value)

		return this.clinicList.filter(v => this.normalizeValue(`${v.accountName} ${v.fullAddress}`).includes(filterValue))
	}

	private normalizeValue(value: string): string {
		return value?.toLowerCase().replace(/\s/g, '')
	}

	// eslint-disable-next-line @typescript-eslint/member-ordering
	ngOnDestroy(): void {
		this.ngUnsubscribe$.next(true)
		this.ngUnsubscribe$.unsubscribe()
	}
}
